import { useLoading } from '@teko/ds-desktop';
import { ISupermarketTerminal } from '@teko/order';
import { trackCustomEvent } from '@teko/tracking';
import TerraKit from '@terra-js/terra-kit';
import { PopupTerminalPicker } from 'components/Terminal';
import { useGlobalData } from 'contexts/GlobalDataContext';
import { useServices } from 'contexts/ServiceContext';
import {
  clearCacheAndReloadPage,
  getJson,
  getLocalStorageValue,
  getUserSelectedInfo,
  reloadPage,
  removeUserSelectedInfo,
  setLocalStorageValue,
  setUserSelectedInfo,
} from 'helpers';
import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { userSelectedConstants } from '../../constants';

export interface DistanceTerminalContext {
  currentTerminal?: ISupermarketTerminal;
  currentTerminals?: ISupermarketTerminal[];
  setLoadingGetDistanceTerminals?: (loading: boolean) => void;
  isLoadingGetDistanceTerminals?: boolean;
  setLocationGetDistanceTerminals?: (location: Location) => void;
  locationGetDistanceTerminals?: Location;
  isGetCurrentLocation?: boolean;
  setIsGetCurrentLocation?: (isGetCurrentLocation: boolean) => void;
  isModeEnterAddress?: boolean;
  setIsModeEnterAddress?: (isModeEnterAddress: boolean) => void;
  showTerminalPicker?: boolean;
  onCloseTerminalPicker?: () => void;
  onOpenTerminalPicker?: () => void;
  getDistanceLocation?: (addressInfo: {
    address?: string;
    lat?: number;
    long?: number;
  }) => Promise<ISupermarketTerminal[]>;
  setNewTerminal?: (
    terminal: ISupermarketTerminal,
    noNeedReload?: boolean,
    reloadUrl?: string,
  ) => void;
  messageError?: string;
  setMessageError?: Dispatch<SetStateAction<string>>;
  setLoadingGetDistanceTerminal?: (loading: boolean) => void;
  overrideTerminal?: (
    location: Location,
    terminal: ISupermarketTerminal,
    terminals: ISupermarketTerminal[],
    noNeedReload?: boolean,
    reloadUrl?: string,
  ) => void;
  permissionGetLocationStatus?: string;
  temporaryDataDistanceTerminal: TemporaryDataTerminalPicker;
  setTemporaryDataDistanceTerminal?: Dispatch<
    SetStateAction<TemporaryDataTerminalPicker>
  >;
}
export interface Location {
  address: string;
  provinceCode: string;
  districtCode?: string;
  wardCode: string;
  provinceName: string;
  wardName: string;
  districtName?: string;
  lat?: number;
  long?: number;
}

export interface TemporaryDataTerminalPicker {
  location: {
    address: string;
    provinceCode: string;
    districtCode?: string;
    wardCode: string;
    provinceName: string;
    wardName: string;
    districtName?: string;
    lat?: number;
    long?: number;
  };
  terminals: ISupermarketTerminal[];
}

const defaultTemporaryData = {
  location: {
    address: '',
    provinceCode: '',
    wardCode: '',
    provinceName: '',
    wardName: '',
    districtCode: '',
    districtName: '',
  },
  terminals: [],
};

const DistanceTerminalContext = createContext<DistanceTerminalContext>({
  temporaryDataDistanceTerminal: defaultTemporaryData,
});

interface DistanceTerminalProviderProps {
  children: React.ReactNode;
}

export const DistanceTerminalProvider = ({
  children,
}: DistanceTerminalProviderProps) => {
  const {
    epicOmni1584Phase2,
    isDmsFlow,
    platformId,
    isMobile,
    clearCacheAfterSwitchLocation,
    isAllowGetTerminalFromTerra,
    terminalId,
  } = useGlobalData();
  const { orderService } = useServices();

  const [loading, setLoading] = useState(false);
  const [, setLoadingGetDistanceTerminal] = useLoading('distanceTerminal');

  const [currentTerminal, setCurrentTerminal] = useState<
    ISupermarketTerminal | undefined
  >(undefined);
  const [currentTerminals, setCurrentTerminals] = useState<
    ISupermarketTerminal[]
  >([]);
  const [showTerminalPicker, setShowTerminalPicker] = useState<boolean>(false);
  const [isGetCurrentLocation, setIsGetCurrentLocation] = useState(false);
  const [isModeEnterAddress, setIsModeEnterAddress] = useState(true);
  const [messageError, setMessageError] = useState('');
  const [permissionStatus, setPermissionStatus] = useState('denied');

  const [temporaryData, setTemporaryData] =
    useState<TemporaryDataTerminalPicker>(defaultTemporaryData);

  const [location, setLocation] = useState<Location>({
    address: '',
    provinceCode: '',
    wardCode: '',
    provinceName: '',
    wardName: '',
    districtCode: '',
    districtName: '',
  });

  const onOpenTerminalPicker = () => {
    setShowTerminalPicker(true);
    setMessageError('');
  };

  const onCloseTerminalPicker = () => {
    if (!currentTerminal) {
      return;
    }
    setShowTerminalPicker(false);
  };

  const selectTerminal = (terminal: ISupermarketTerminal) => {
    setUserSelectedInfo(
      userSelectedConstants.TERMINAL,
      terminal.terminalCode,
      isMobile,
      true,
    );
    setUserSelectedInfo(
      userSelectedConstants.TERMINAL_NAME,
      terminal.terminalName,
      isMobile,
      true,
    );
    setUserSelectedInfo(
      userSelectedConstants.TERMINAL_ID,
      terminal?.terminalId?.toString(),
      isMobile,
      true,
    );
    setLocalStorageValue(
      userSelectedConstants.USER_LOCATION,
      JSON.stringify(temporaryData.location),
      isMobile,
    );
    setLocalStorageValue(
      userSelectedConstants.TERMINALS,
      JSON.stringify(temporaryData.terminals),
      isMobile,
    );
    setUserSelectedInfo(
      userSelectedConstants.IS_GET_GEOLOCATION,
      JSON.stringify(isGetCurrentLocation),
      isMobile,
      true,
    );
    setCurrentTerminal(terminal);
  };

  const setNewTerminal = (
    terminal: ISupermarketTerminal,
    noNeedReload?: boolean,
    reloadUrl?: string,
  ) => {
    if (!terminal) {
      return;
    }
    selectTerminal(terminal);
    if (!noNeedReload) {
      if (clearCacheAfterSwitchLocation) {
        clearCacheAndReloadPage(reloadUrl);
      } else {
        reloadPage(reloadUrl);
      }
    }
  };

  const overrideTerminal = async (
    location: Location,
    terminal: ISupermarketTerminal,
    terminals: ISupermarketTerminal[],
    noNeedReload?: boolean,
    reloadUrl?: string,
  ) => {
    setUserSelectedInfo(
      userSelectedConstants.TERMINAL,
      terminal.terminalCode,
      isMobile,
      true,
    );
    setUserSelectedInfo(
      userSelectedConstants.TERMINAL_NAME,
      terminal.terminalName,
      isMobile,
      true,
    );
    setUserSelectedInfo(
      userSelectedConstants.TERMINAL_ID,
      terminal?.terminalId?.toString(),
      isMobile,
      true,
    );
    setLocalStorageValue(
      userSelectedConstants.USER_LOCATION,
      JSON.stringify(location),
      isMobile,
    );
    setLocalStorageValue(
      userSelectedConstants.TERMINALS,
      JSON.stringify(terminals),
      isMobile,
    );
    setUserSelectedInfo(
      userSelectedConstants.IS_GET_GEOLOCATION,
      JSON.stringify(false),
      isMobile,
      true,
    );
    setLocation(location);
    setCurrentTerminal(terminal);
    setCurrentTerminals(terminals);
    setIsGetCurrentLocation(false);
    if (!noNeedReload) {
      if (clearCacheAfterSwitchLocation) {
        clearCacheAndReloadPage(reloadUrl);
      } else {
        reloadPage(reloadUrl);
      }
    }
  };

  const getDistanceLocation = async ({
    address,
    lat,
    long,
  }: {
    address?: string;
    lat?: number;
    long?: number;
  }) => {
    try {
      setLoading(true);
      const response = await orderService.getTerminalsByAddress({
        fullAddress: address,
        lat,
        long,
        platformId,
      });
      setLoading(false);
      return response.data.terminals;
    } catch (error) {
      setLoading(false);
      return [];
    }
  };

  const getDataFromLocalStorage = () => {
    const terminalCodeFromStorage = getUserSelectedInfo(
      userSelectedConstants.TERMINAL,
      isMobile,
      true,
    );
    const terminalsFromStorage = getLocalStorageValue(
      userSelectedConstants.TERMINALS,
      isMobile,
    );
    const isGetGeoLocationFromStorage = getUserSelectedInfo(
      userSelectedConstants.IS_GET_GEOLOCATION,
      isMobile,
      true,
    );
    const jsonUserLocationFromStorage = getLocalStorageValue(
      userSelectedConstants.USER_LOCATION,
      isMobile,
    );

    return {
      terminalCodeFromStorage,
      terminalsFromStorage,
      isGetGeoLocationFromStorage,
      jsonUserLocationFromStorage,
    };
  };

  const getAndSaveTerminalFromStorage = async () => {
    trackCustomEvent('Start get data from storage', {
      action: 'get data from terra',
      label: 'Huy Test',
    });
    if (TerraKit.isAvailable() && isAllowGetTerminalFromTerra) {
      trackCustomEvent(
        'Start get terminal Data FromApp when turn on flag 1584',
        {},
      );
      const terminalDataFromApp = await TerraKit.erp().getTerminal();
      trackCustomEvent('Terminal Data FromApp when turn on flag 1584', {
        action: 'get data from terra',
        label: JSON.stringify(terminalDataFromApp),
      });
      if (!!terminalDataFromApp && terminalId !== terminalDataFromApp.id) {
        trackCustomEvent(
          'Start save Terminal Data FromApp when turn on flag 1584',
          {
            action: 'get data from terra',
            label: `${terminalDataFromApp} + ${terminalDataFromApp.id}`,
          },
        );
        setNewTerminal({
          terminalCode: terminalDataFromApp.code,
          terminalId: terminalDataFromApp.id,
          terminalName: terminalDataFromApp.name || '',
          siteId: 0,
          fullAddress: '',
          distance: 0,
          provinceCode: '',
        });
      }
      return;
    }
    const {
      terminalCodeFromStorage,
      terminalsFromStorage,
      isGetGeoLocationFromStorage,
      jsonUserLocationFromStorage,
    } = getDataFromLocalStorage();
    if (!!isGetGeoLocationFromStorage) {
      setIsGetCurrentLocation(getJson(isGetGeoLocationFromStorage));
    }
    if (!!terminalsFromStorage) {
      setCurrentTerminals(getJson(terminalsFromStorage) || []);
    }

    if (!!jsonUserLocationFromStorage) {
      const userLocationFromStorage = getJson(jsonUserLocationFromStorage);
      if (!!userLocationFromStorage) {
        setLocation(userLocationFromStorage);
      }
    }
    if (!!terminalCodeFromStorage && !!terminalsFromStorage) {
      const terminals = getJson(terminalsFromStorage) || [];
      const terminal = terminals.find(
        (item: ISupermarketTerminal) =>
          item.terminalCode === terminalCodeFromStorage,
      );
      setCurrentTerminal(terminal);
    }
    if (!!terminalsFromStorage && !!jsonUserLocationFromStorage) {
      const userLocationFromStorage = getJson(jsonUserLocationFromStorage);
      const terminals = getJson(terminalsFromStorage) || [];

      setTemporaryData({
        location: userLocationFromStorage,
        terminals: terminals || [],
      });
    }
  };

  useEffect(() => {
    if ('geolocation' in navigator && 'permissions' in navigator) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then((result) => {
          setPermissionStatus(result.state); // 'granted', 'prompt', or 'denied'
        })
        .catch(() => {
          setPermissionStatus('denied');
        });
    } else {
      setPermissionStatus('denied');
    }
  }, []);

  useEffect(() => {
    const { jsonUserLocationFromStorage } = getDataFromLocalStorage();
    if (!isDmsFlow && epicOmni1584Phase2) {
      getAndSaveTerminalFromStorage();
    } else {
      // only remove location & terminal info and reload page If level location is set before (epicOmni1584 were enabled before)
      if (!!jsonUserLocationFromStorage && !epicOmni1584Phase2) {
        removeUserSelectedInfo(userSelectedConstants.TERMINAL, isMobile);
        removeUserSelectedInfo(userSelectedConstants.TERMINAL_ID, isMobile);
        removeUserSelectedInfo(userSelectedConstants.TERMINAL_NAME, isMobile);
        removeUserSelectedInfo(userSelectedConstants.USER_LOCATION, isMobile);
        reloadPage();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleScrollOrClickWhenFirstTimeVisitWeb = () => {
      const {
        terminalCodeFromStorage,
        terminalsFromStorage,
        jsonUserLocationFromStorage,
      } = getDataFromLocalStorage();
      if (
        (!terminalCodeFromStorage ||
          !terminalsFromStorage ||
          !jsonUserLocationFromStorage) &&
        !showTerminalPicker &&
        !TerraKit.isAvailable()
      ) {
        // trong TH la webview thi se khong can hien popup ma tuyen tu app xuong
        // duoc xu ly o ham getAndSaveTerminalFromStorage o tren
        onOpenTerminalPicker();
        trackCustomEvent('visitWebsiteInTheFirstTime', {});
      }
    };
    window.addEventListener('scroll', handleScrollOrClickWhenFirstTimeVisitWeb);
    document.body.addEventListener(
      'click',
      handleScrollOrClickWhenFirstTimeVisitWeb,
    );

    return () => {
      window.removeEventListener(
        'scroll',
        handleScrollOrClickWhenFirstTimeVisitWeb,
      );
      document.body.removeEventListener(
        'click',
        handleScrollOrClickWhenFirstTimeVisitWeb,
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <DistanceTerminalContext.Provider
      value={{
        setLoadingGetDistanceTerminals: setLoading,
        isLoadingGetDistanceTerminals: loading,
        setLocationGetDistanceTerminals: setLocation,
        locationGetDistanceTerminals: location,
        currentTerminal,
        currentTerminals,
        showTerminalPicker,
        onOpenTerminalPicker,
        onCloseTerminalPicker,
        isGetCurrentLocation,
        setIsGetCurrentLocation,
        getDistanceLocation,
        isModeEnterAddress,
        setIsModeEnterAddress,
        setNewTerminal,
        messageError,
        setMessageError,
        setLoadingGetDistanceTerminal,
        overrideTerminal,
        permissionGetLocationStatus: permissionStatus,
        temporaryDataDistanceTerminal: temporaryData,
        setTemporaryDataDistanceTerminal: setTemporaryData,
      }}
    >
      {children}
      {!isDmsFlow && !!epicOmni1584Phase2 && (
        <PopupTerminalPicker
          showPopup={showTerminalPicker}
          onClosePopup={onCloseTerminalPicker}
        />
      )}
    </DistanceTerminalContext.Provider>
  );
};

export const useDistanceTerminalContext = () => {
  return useContext(DistanceTerminalContext);
};
